import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Paintbrush } from "lucide-react";

export default function BrandingSettingsPage() {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    // Defence-in-depth: if the user has no organisation_id and isn't a
    // super_admin, the /orgs endpoint will either return [] or 403.
    // Don't hang on a spinner — show the empty state immediately so
    // they can navigate to onboarding.  This was the production
    // "Branding tab stuck loading forever" symptom.
    if (!user) return;
    if (user.role !== "super_admin" && !user.organization_id) {
      setLoading(false);
      return;
    }
    api
      .get("/orgs")
      .then((r) => {
        const myOrg =
          user.role === "super_admin"
            ? r.data[0]
            : r.data.find((o) => o.id === user.organization_id) || null;
        setOrg(myOrg);
        if (myOrg) setForm(myOrg);
      })
      .catch((err) => {
        setError(formatApiError(err.response?.data?.detail) || err.message || "Could not load branding.");
      })
      .finally(() => setLoading(false));
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch(`/orgs/${org.id}`, form);
      setOrg(data);
      toast.success("Branding saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div
          className="flex items-center gap-3 text-slate-500"
          data-testid="branding-loading"
        >
          <div className="h-3 w-3 rounded-full bg-[#D4AF37] animate-pulse" />
          <span>Loading branding…</span>
        </div>
      </DashboardLayout>
    );
  }

  if (!org) {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto py-20 text-center" data-testid="branding-empty-state">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            White-label
          </p>
          <h1 className="mt-3 font-display text-4xl text-[#0A192F]">
            No organisation <span className="italic font-medium text-[#D4AF37]">linked yet.</span>
          </h1>
          <p className="mt-4 text-slate-500 max-w-md mx-auto">
            {error || (
              "Branding lives on your organisation profile. Create or join one to lift watermarks, swap colours, and put your logo on every certificate."
            )}
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Link to="/onboarding/organisation">
              <Button
                className="bg-[#0F4C81] hover:bg-[#0A3860] text-white"
                data-testid="branding-empty-onboarding-cta"
              >
                Set up your organisation
              </Button>
            </Link>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
          White-label
        </p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
          Brand & theme
        </h1>

        <div className="mt-10 grid lg:grid-cols-2 gap-8">
          <form onSubmit={save} className="space-y-5" data-testid="branding-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Organisation name</Label>
                <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="branding-name-input" />
              </div>
              <div>
                <Label>Slug</Label>
                <Input value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Tagline</Label>
              <Input value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="branding-tagline-input" />
            </div>
            <div>
              <Label>Logo URL</Label>
              <Input value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Primary color</Label>
                <div className="flex gap-2 items-center mt-2">
                  <Input type="color" value={form.primary_color || "#0F4C81"} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-12 w-16 p-1" data-testid="branding-primary-color" />
                  <Input value={form.primary_color || ""} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="font-mono h-12" />
                </div>
              </div>
              <div>
                <Label>Secondary color</Label>
                <div className="flex gap-2 items-center mt-2">
                  <Input type="color" value={form.secondary_color || "#D4AF37"} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} className="h-12 w-16 p-1" />
                  <Input value={form.secondary_color || ""} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} className="font-mono h-12" />
                </div>
              </div>
            </div>
            <div>
              <Label>Contact email</Label>
              <Input type="email" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
            </div>
            <div>
              <Label>Plan</Label>
              <Input value={form.plan || ""} onChange={(e) => setForm({ ...form, plan: e.target.value })} />
            </div>
            <Button type="submit" className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="branding-save-button">
              Save changes
            </Button>
          </form>

          {/* Preview */}
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="p-6 border-b border-slate-200 flex items-center gap-3">
              <Paintbrush className="h-4 w-4 text-[#D4AF37]" />
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                Live preview
              </p>
            </div>
            <div
              className="h-32"
              style={{
                background: `linear-gradient(120deg, ${form.primary_color || "#0F4C81"}, ${form.secondary_color || "#D4AF37"})`,
              }}
            />
            <div className="p-6 -mt-10">
              <div
                className="h-14 w-14 rounded-lg border-4 border-white bg-white flex items-center justify-center font-serif text-2xl font-bold"
                style={{ color: form.primary_color || "#0F4C81" }}
              >
                {(form.name || "?")[0]}
              </div>
              <h3 className="mt-4 text-xl font-semibold tracking-tight text-[#0A192F]">
                {form.name || "Your organisation"}
              </h3>
              <p className="text-sm text-slate-500 font-serif italic mt-1">{form.tagline || "Tagline goes here"}</p>
              <div className="mt-5 flex items-center gap-3">
                <button
                  className="px-4 py-2 rounded-md text-white text-xs uppercase tracking-[0.18em] font-bold"
                  style={{ background: form.primary_color || "#0F4C81" }}
                >
                  Register
                </button>
                <button
                  className="px-4 py-2 rounded-md text-[#0A192F] text-xs uppercase tracking-[0.18em] font-bold"
                  style={{ background: form.secondary_color || "#D4AF37" }}
                >
                  Learn more
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
